import { useState, useEffect } from 'react';
import { optimizeSvg } from '../utils/svgOptimizer';

interface UseSvgPreloadOptions {
  onLoad?: () => void;
  onError?: (error: Error) => void;
  cacheKey?: string;
}

export const useSvgPreload = (src: string, options: UseSvgPreloadOptions = {}) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) return;

    // Check if SVG is already cached
    const cacheKey = options.cacheKey || src;
    const cachedSvg = sessionStorage.getItem(`svg_${cacheKey}`);
    
    if (cachedSvg) {
      setSvgContent(cachedSvg);
      setIsLoaded(true);
      setHasError(false);
      options.onLoad?.();
      return;
    }

    const fetchSvg = async () => {
      try {
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error(`Failed to fetch SVG: ${response.status}`);
        }
        const svgText = await response.text();
        
        // Optimize the SVG content
        const optimizedSvg = optimizeSvg(svgText);
        
        // Cache the optimized SVG content
        sessionStorage.setItem(`svg_${cacheKey}`, optimizedSvg);
        
        setSvgContent(optimizedSvg);
        setIsLoaded(true);
        setHasError(false);
        options.onLoad?.();
      } catch (error) {
        console.error('Error loading SVG:', error);
        setHasError(true);
        setIsLoaded(false);
        options.onError?.(error as Error);
      }
    };

    fetchSvg();
  }, [src, options]);

  return { svgContent, isLoaded, hasError };
}; 